<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import AgenteIaAPI from 'dashboard/api/agenteIa';
import AgenteIaLayout from '../components/AgenteIaLayout.vue';

const { t } = useI18n();
const status = ref(null);
const oferta = ref(null);
const carregando = ref(true);
const enviando = ref('');

const carregar = async () => {
  carregando.value = true;
  const [statusResp, ofertaResp] = await Promise.all([
    AgenteIaAPI.status(),
    AgenteIaAPI.oferta(),
  ]);
  status.value = statusResp.data;
  oferta.value = ofertaResp.data;
  carregando.value = false;
};

onMounted(carregar);

const planoAtual = computed(() => status.value?.vinculo?.plano || 'agente');
const outroPlano = computed(() =>
  planoAtual.value === 'agente' ? 'agente_avancado' : 'agente'
);
const limites = computed(() => status.value?.plano?.limites || {});
const ofertaAtiva = computed(() => !!oferta.value?.ativa);
const planoOfertado = computed(() => oferta.value?.planos?.[outroPlano.value]);
const operadorLigado = computed(() => !!status.value?.vinculo?.operador_funil);
const moeda = valor =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    valor || 0
  );
const dataFormatada = valor =>
  new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(valor));

const pedidoPendente = tipo =>
  oferta.value?.pedidos_pendentes?.find(pedido => pedido.tipo === tipo);
const pedidoDePlano = computed(() => pedidoPendente('plano'));
const pedidoDeOperador = computed(() => pedidoPendente('operador'));

const pedir = async (tipo, valor) => {
  enviando.value = tipo;
  try {
    const { data } = await AgenteIaAPI.pedir(tipo, valor);
    oferta.value = { ...oferta.value, pedidos_pendentes: data.pedidos };
  } finally {
    enviando.value = '';
  }
};
</script>

<template>
  <AgenteIaLayout
    :title="t('AGENTE_IA.PLANO.TITULO')"
    :description="t('AGENTE_IA.PLANO.DESCRICAO')"
  >
    <p v-if="carregando" class="text-sm text-n-slate-11">
      {{ t('AGENTE_IA.CARREGANDO') }}
    </p>
    <template v-else>
      <div class="p-4 rounded-lg bg-n-alpha-black2">
        <h2 class="text-sm font-medium text-n-slate-12">
          {{ t(`AGENTE_IA.PLANOS.${planoAtual.toUpperCase()}`) }}
        </h2>
        <ul class="mt-2 text-sm list-disc list-inside text-n-slate-11">
          <li>
            {{ t('AGENTE_IA.PLANO.LIMITE_AGENTES', { n: limites.personas }) }}
          </li>
          <li>{{ t('AGENTE_IA.PLANO.LIMITE_BASES', { n: limites.bases }) }}</li>
          <li>
            {{
              t('AGENTE_IA.PLANO.LIMITE_DOCUMENTOS', { n: limites.documentos })
            }}
          </li>
          <li>
            {{
              t('AGENTE_IA.PLANO.LIMITE_FOLLOW_UP', {
                n: limites.passos_follow_up,
              })
            }}
          </li>
          <li>
            {{
              t('AGENTE_IA.PLANO.LIMITE_FERRAMENTAS', {
                n: limites.max_tool_calls,
              })
            }}
          </li>
        </ul>
        <p v-if="status.teto" class="mt-2 text-xs text-n-slate-11">
          {{
            t('AGENTE_IA.TETO.USO', {
              gasto: moeda(status.teto.gasto),
              teto: moeda(status.teto.valor),
              percentual: status.teto.percentual,
            })
          }}
        </p>
      </div>

      <template v-if="ofertaAtiva">
        <div
          v-if="planoOfertado"
          class="flex flex-col gap-2 p-4 border rounded-lg border-n-weak"
        >
          <h2 class="text-sm font-medium text-n-slate-12">
            {{ t(`AGENTE_IA.PLANOS.${outroPlano.toUpperCase()}`) }} —
            {{ moeda(planoOfertado.preco) }}
          </h2>
          <ul class="text-sm list-disc list-inside text-n-slate-11">
            <li v-for="destaque in planoOfertado.destaques" :key="destaque">
              {{ destaque }}
            </li>
          </ul>
          <p v-if="pedidoDePlano" class="text-sm text-n-teal-11">
            {{
              t('AGENTE_IA.PLANO.PEDIDO_ENVIADO', {
                data: dataFormatada(pedidoDePlano.criado_em),
              })
            }}
          </p>
          <button
            v-else
            class="self-start button"
            :disabled="enviando === 'plano'"
            @click="pedir('plano', outroPlano)"
          >
            {{ t('AGENTE_IA.PLANO.QUERO_ESTE_PLANO') }}
          </button>
        </div>

        <div
          v-if="oferta.operador_funil && !operadorLigado"
          class="flex flex-col gap-2 p-4 border rounded-lg border-n-weak"
        >
          <h2 class="text-sm font-medium text-n-slate-12">
            {{ t('AGENTE_IA.NAV.OPERADOR') }} —
            {{ moeda(oferta.operador_funil.preco) }}
          </h2>
          <ul class="text-sm list-disc list-inside text-n-slate-11">
            <li
              v-for="destaque in oferta.operador_funil.destaques"
              :key="destaque"
            >
              {{ destaque }}
            </li>
          </ul>
          <p v-if="pedidoDeOperador" class="text-sm text-n-teal-11">
            {{
              t('AGENTE_IA.PLANO.PEDIDO_ENVIADO', {
                data: dataFormatada(pedidoDeOperador.criado_em),
              })
            }}
          </p>
          <button
            v-else
            class="self-start button"
            :disabled="enviando === 'operador'"
            @click="pedir('operador')"
          >
            {{ t('AGENTE_IA.PLANO.QUERO_O_OPERADOR') }}
          </button>
        </div>

        <p
          v-if="oferta.contato?.email || oferta.contato?.whatsapp"
          class="text-xs text-n-slate-11"
        >
          {{ t('AGENTE_IA.PLANO.CONTATO') }}
          <span v-if="oferta.contato.email">{{ oferta.contato.email }}</span>
          <span v-if="oferta.contato.whatsapp">
            · {{ oferta.contato.whatsapp }}</span
          >
        </p>
      </template>
    </template>
  </AgenteIaLayout>
</template>
